import { useState, useEffect } from "react";
import TablePagination from "./TablePagination";
import { IconButton, Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import {
  getAllInActiveAbbreviations,
  getAllInActiveAlias,
  getAllInActiveAnglicized,
  getAllInActiveBadData,
  getAllInActiveSingleDouble,
  getAllInActiveInitials,
  getAllInActiveNicknames,
  getAllInActiveIntervening,
  getAllInActiveCountries,
} from "../../../Api";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

function ViewHistoryModal({
  open,
  onClose,
  currentPage,
  setCurrentPage,
  selectedTab,
}) {
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const navigate = useNavigate();

  const itemsPerPage = 5;
  // Get sortable fields based on tab
  const getSortableFields = () => {
    const baseFields = {
      0: ["ABBREVIATION", "REPLACE_WORD"], // Abbreviations
      1: ["NAME", "ALIAS_NAME"], // Aliases
      2: ["NAME", "ANGLICIZED_NAME"], // Anglicized
      3: ["BAD_DATA"], // Bad Data
      4: ["LETTER_CODE", "REPLACE_LETTER_CODE"], // Double Letters
      5: ["NAME", "INITIALS"], // Initials
      6: ["NAME", "NICKNAME"], // Nicknames
      7: ["INTERVENING_WORDS"], // Intervening
      8: ["COUNTRY_NAME"], // Countries
    };
    return [...(baseFields[selectedTab] || []), "UPDATED_DATE"];
  };

  const handleSort = (columnKey) => {
    const sortableFields = getSortableFields();
    const normalizedKey = columnKey.toUpperCase().replace(/\s+/g, "_");

    if (!sortableFields.includes(normalizedKey)) {
      return;
    }

    setSortConfig((prevConfig) => {
      if (prevConfig.key === normalizedKey) {
        return {
          key: normalizedKey,
          direction: prevConfig.direction === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          key: normalizedKey,
          direction: "asc",
        };
      }
    });
    setCurrentPage(1);
  };

  const renderSortIcon = (columnKey) => {
    const sortableFields = getSortableFields();
    const normalizedKey = columnKey.toUpperCase().replace(/\s+/g, "_");

    if (!sortableFields.includes(normalizedKey)) {
      return null;
    }

    const isActive = sortConfig.key === normalizedKey;
    const isAsc = sortConfig.direction === "asc";

    if (!isActive)
      return (
        <FaSort
          className="sort-icon inactive"
          style={{ marginLeft: 4, opacity: 0.5 }}
        />
      );

    return isAsc ? (
      <FaSortUp className="sort-icon active" style={{ marginLeft: 4 }} />
    ) : (
      <FaSortDown className="sort-icon active" style={{ marginLeft: 4 }} />
    );
  };

  const sortData = (data) => {
    if (!sortConfig.key || !data || data.length === 0) return data;

    const sortedData = [...data].sort((a, b) => {
      const getValue = (row, key) => {
        const keyMappings = {
          ABBREVIATION: ["name"],
          REPLACE_WORD: ["abbreviations"],
          NAME: ["name", "entity_name"],
          ALIAS_NAME: ["alias_word"],
          ANGLICIZED_NAME: ["anglicized_word"],
          BAD_DATA: ["special_char"],
          LETTER_CODE: ["letter"],
          REPLACE_LETTER_CODE: ["replace_letter"],
          INITIALS: ["initial"],
          NICKNAME: ["nick_name"],
          INTERVENING_WORDS: ["word_list"],
          COUNTRY_NAME: ["country"],
          UPDATED_DATE: ["updated_date"],
          CREATED_DATE: ["created_date"],
        };

        const possibleKeys = keyMappings[key] || [key.toLowerCase()];

        for (const possibleKey of possibleKeys) {
          if (row[possibleKey] !== undefined && row[possibleKey] !== null) {
            return row[possibleKey];
          }
        }
        return "";
      };

      const aValue = getValue(a, sortConfig.key);
      const bValue = getValue(b, sortConfig.key);

      // Handle date values
      if (
        sortConfig.key === "UPDATED_DATE" ||
        sortConfig.key === "CREATED_DATE"
      ) {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
        }
      }

      // Handle string comparison
      const aStr = String(aValue || "").toLowerCase();
      const bStr = String(bValue || "").toLowerCase();
      const comparison = aStr.localeCompare(bStr);

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });

    return sortedData;
  };

  // Reset current page to 1 when selectedTab changes
  useEffect(() => {
    setCurrentPage(1);
    setSortConfig({ key: null, direction: "asc" });
  }, [selectedTab, setCurrentPage]);

  useEffect(() => {
    const fetchHistoryData = async () => {
      if (!open) return;

      setIsLoading(true);
      setError("");
      try {
        let response = { status: "F", result_code: 0, result_info: [] };

        switch (selectedTab) {
          case 0: // Abbreviations
            response = await getAllInActiveAbbreviations();
            break;
          case 1: // Aliases
            response = await getAllInActiveAlias();
            break;
          case 2: // Anglicized
            response = await getAllInActiveAnglicized();
            break;
          case 3: // Bad Data
            response = await getAllInActiveBadData();
            break;
          case 4: // Double Letters
            response = await getAllInActiveSingleDouble();
            break;
          case 5: // Initial
            response = await getAllInActiveInitials();
            break;
          case 6: // Nick Name
            response = await getAllInActiveNicknames();
            break;
          case 7: // Intervening
            response = await getAllInActiveIntervening();
            break;
          case 8: // Countries
            response = await getAllInActiveCountries();
            break;
          default:
            setHistoryData([]);
            return;
        }

        if (response.status === "S" && response.result_code === 200) {
          setHistoryData(response.result_info || []);
        } else if (response.status === "E") {
          Swal.fire({
            icon: "warning",
            text:
              response.message ||
              "An error occurred while fetching history data.",
            showConfirmButton: false,
            timer: 3000,
          });
          setHistoryData([]);
        } else if (response.status === "F") {
          if (
            response.message === "Unauthorized - Missing token" ||
            response.message === "Unauthorized - Invalid token"
          ) {
            Swal.fire({
              text: "Please login with your credentials",
              icon: "warning",
              showConfirmButton: false,
              timer: 3000,
            }).then(() => {
              localStorage.clear();
              navigate("/login");
            });
          }
        } else {
          setError("Failed to fetch history data");
          setHistoryData([]);
        }
      } catch (err) {
        setError("An error occurred while fetching history data");
        setHistoryData([]);
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistoryData();
  }, [open, selectedTab, navigate]);

  const getColumns = () => {
    switch (selectedTab) {
      case 0: // Abbreviations
        return [
          { key: "name", label: "Abbreviation", sortKey: "ABBREVIATION" },
          {
            key: "abbreviations",
            label: "Replace Word",
            sortKey: "REPLACE_WORD",
          },
        ];
      case 1: // Aliases
        return [
          { key: "name", label: "Name", sortKey: "NAME" },
          { key: "alias_word", label: "Alias Name", sortKey: "ALIAS_NAME" },
          { key: "alias_type", label: "Alias Type", sortKey: "ALIAS_TYPE" },
        ];
      case 2: // Anglicized
        return [
          { key: "entity_name", label: "Name", sortKey: "NAME" },
          {
            key: "anglicized_word",
            label: "Anglicized Name",
            sortKey: "ANGLICIZED_NAME",
          },
        ];
      case 3: // Bad Data
        return [
          { key: "special_char", label: "Bad_Data", sortKey: "BAD_DATA" },
        ];
      case 4: // Double Letters
        return [
          { key: "letter", label: "Letter Code", sortKey: "LETTER_CODE" },
          {
            key: "replace_letter",
            label: "Replace Letter Code",
            sortKey: "REPLACE_LETTER_CODE",
          },
        ];
      case 5: // Initial
        return [
          { key: "name", label: "Name", sortKey: "NAME" },
          { key: "initial", label: "Initials", sortKey: "INITIALS" },
        ];
      case 6: // Nick Name
        return [
          { key: "name", label: "Name", sortKey: "NAME" },
          { key: "nick_name", label: "Nickname", sortKey: "NICKNAME" },
        ];
      case 7: // Intervening
        return [
          {
            key: "word_list",
            label: "Intervening Words",
            sortKey: "INTERVENING_WORDS",
          },
        ];
      case 8: // Countries
        return [
          { key: "country", label: "Country Name", sortKey: "COUNTRY_NAME" },
        ];
      default:
        return [];
    }
  };

  const columns = getColumns();

  const sortableFields = getSortableFields();

  // Apply sorting to history data
  const sortedHistoryData = sortData(historyData);

  const totalItems = sortedHistoryData.length;
  const totalPages =
    error || isLoading ? 0 : Math.ceil(totalItems / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedHistoryData.slice(startIndex, endIndex);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="history-modal-title"
      aria-describedby="history-modal-description"
    >
      <div className="modal-overlay">
        <div
          className="modal-container bg-white"
          style={{
            maxWidth: "90%",
            width: "90%",
            maxHeight: "90vh",
            height: "auto",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 id="history-modal-title">View History</h5>
            <IconButton
              onClick={onClose}
              color="black"
              sx={{ fontWeight: 600 }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-striped history-table">
              <thead className="table-dark">
                <tr>
                  <th>Id</th>
                  {columns.map((col) => {
                    // Use sortKey from column definition instead of normalizing col.key
                    const sortKey =
                      col.sortKey || col.key.toUpperCase().replace(/\s+/g, "_");
                    const isSortable = sortableFields.includes(sortKey);

                    return (
                      <th
                        key={col.key}
                        onClick={() =>
                          isSortable ? handleSort(sortKey) : undefined
                        }
                        style={{
                          cursor: isSortable ? "pointer" : "default",
                          userSelect: "none",
                          position: "relative",
                        }}
                      >
                        {col.label}
                        {isSortable ? renderSortIcon(sortKey) : null}
                      </th>
                    );
                  })}
                  <th>Created By</th>
                  <th>Action By</th>
                  <th>Uploaded Date</th>
                  <th
                    onClick={() => handleSort("UPDATED_DATE")}
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      position: "relative",
                    }}
                  >
                    Action At
                    {renderSortIcon("UPDATED_DATE")}
                  </th>
                  <th>Action Type</th>
                  {/* <th
                    
                    
                  >
                    Updated Date
                    
                  </th> */}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={columns.length + 7} className="text-center">
                      <div className="d-flex justify-content-center">
                        <img
                          src={require("../../../assets/loader.gif")}
                          alt="loader"
                          style={{ width: "50px" }}
                        />
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={columns.length + 7}
                      className="text-center text-danger"
                    >
                      {error}
                    </td>
                  </tr>
                ) : currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 7} className="text-center">
                      No history data available
                    </td>
                  </tr>
                ) : (
                  currentItems.map((item, index) => (
                    <tr key={item.record_id || index}>
                      <td>{startIndex + index + 1}</td>
                      {columns.map((col) => (
                        <td key={col.key}>{item[col.key] || ""}</td>
                      ))}
                      <td>{item.created_by || ""}</td>
                      <td>{item.action_by_name || ""}</td>
                      {/* <td>{item.uploaded_date || ""}</td> */}
                      <td>
                        {item.created_date
                          ? dayjs(item.created_date).format(
                              "DD-MM-YYYY HH:mm:ss"
                            )
                          : ""}
                      </td>
                      <td>
                        {item.updated_date
                          ? dayjs(item.updated_date).format(
                              "DD-MM-YYYY HH:mm:ss"
                            )
                          : ""}
                      </td>
                      <td>{item.action_type || ""}</td>
                      {/* <td>
                        {item.updated_date
                          ? dayjs(item.updated_date).format(
                              "DD-MM-YYYY, HH:mm:ss"
                            )
                          : ""}
                      </td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-end mt-3">
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              handleFirstPage={() => setCurrentPage(1)}
              handlePrevPage={() =>
                setCurrentPage((prev) => Math.max(prev - 1, 1))
              }
              handleNextPage={() => setCurrentPage((prev) => prev + 1)}
              handleLastPage={() => setCurrentPage(totalPages)}
            />
          </div>

          <div className="d-flex justify-content-end mt-3">
            <button className="btn createButton" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default ViewHistoryModal;
